import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  startGuestPlacementSession,
  startPlacementSession,
  requestGuestPlacementEmailCode,
  verifyGuestPlacementEmailCode,
} from "../../../redux/Actions/placementSecurityActions";
import { resetStudentAnswers } from "../../../redux/Actions/studentPlacementAnswerActions";
import Meta from "../../Meta/Meta";
import { useTranslation } from "react-i18next";
import { shouldRequirePlacementFullscreen } from "../../../utils/placementFullscreen";
import placementPolicyContent from "./placementPolicyContent";

const PlacementTestRules = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(["ui"]);
  const isArabic = i18n.language?.startsWith("ar");
  const isGerman = i18n.language?.startsWith("de");
  const hasAccount = !!localStorage.getItem("roleName");

  const [fullscreenError, setFullscreenError] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [loginRequired, setLoginRequired] = useState(false);
  const [issueType, setIssueType] = useState("start");
  const [guestInfo, setGuestInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  });
  const [emailVerificationOpen, setEmailVerificationOpen] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState("");
  const [emailVerificationToken, setEmailVerificationToken] = useState("");
  const [emailVerificationBusy, setEmailVerificationBusy] = useState(false);
  const [emailVerificationError, setEmailVerificationError] = useState("");
  const targetLevel = searchParams.get("targetLevel") || "A2";
  const policyLanguage = isArabic ? "ar" : i18n.language?.startsWith("de") ? "de" : "en";
  const policy = placementPolicyContent[policyLanguage];

  const startAndGo = async (verificationToken = emailVerificationToken) => {
    const res = hasAccount
      ? await dispatch(startPlacementSession(targetLevel))
      : await dispatch(
          startGuestPlacementSession({
            ...guestInfo,
            targetLevel,
            emailVerificationToken: verificationToken,
          })
        );
    if (!res?.ok) {
      setIssueType("start");
      const needsLogin = hasAccount && (res?.status === 401 || res?.status === 403);
      setLoginRequired(needsLogin);
      setErrorText(
        (needsLogin
          ? (isArabic
              ? "انتهت جلسة تسجيل الدخول. يرجى تسجيل الدخول ثم بدء الاختبار مرة أخرى."
              : "Your login session has expired. Please log in and start the placement test again.")
          : res?.error) ||
          (isArabic ? "فشل بدء الجلسة. يرجى المحاولة مرة أخرى أو التواصل معنا." : "Failed to start session. Please try again or contact us.")
      );
      setFullscreenError(true);
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      return;
    }
    dispatch(resetStudentAnswers());
    navigate(`/placement/test?targetLevel=${encodeURIComponent(targetLevel)}`);
  };

  const handleStartTest = async (verificationToken = emailVerificationToken) => {
    setLoginRequired(false);
    if (!hasAccount) {
      const missing = ["firstName", "lastName", "email", "phoneNumber"].some(
        (key) => !guestInfo[key]?.trim()
      );
      if (missing) {
        setErrorText("Please enter first name, last name, email, and phone number before starting.");
        setFullscreenError(true);
        return;
      }
      if (!verificationToken) {
        setEmailVerificationBusy(true);
        setEmailVerificationError("");
        const result = await requestGuestPlacementEmailCode(guestInfo.email);
        setEmailVerificationBusy(false);
        if (!result.ok) {
          setErrorText(result.error);
          setFullscreenError(true);
          return;
        }
        setEmailVerificationCode("");
        setEmailVerificationOpen(true);
        return;
      }
    }
    const requireFullscreen = shouldRequirePlacementFullscreen();

    try {
      if (!requireFullscreen) {
        await startAndGo(verificationToken);
      } else if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setTimeout(async () => {
          if (document.fullscreenElement) {
            await startAndGo(verificationToken);
          } else {
            setIssueType("fullscreen");
            setErrorText(
              t("placementRules.dialogBody", {
                defaultValue: isArabic
                  ? "يرجى السماح بوضع ملء الشاشة لبدء اختبار تحديد المستوى."
                  : "Please allow fullscreen mode to start the placement test.",
              })
            );
            setFullscreenError(true);
          }
        }, 300);
      } else {
        await startAndGo();
      }
    } catch (err) {
      setIssueType("fullscreen");
      console.error("Fullscreen or session start failed:", err);
      setErrorText(
        err?.message ||
          (isArabic
            ? "فشل وضع ملء الشاشة أو بدء الجلسة"
            : "Fullscreen or session start failed")
      );
      setFullscreenError(true);
    }
  };

  const verifyGuestEmailAndStart = async () => {
    if (!emailVerificationCode.trim()) return;
    setEmailVerificationBusy(true);
    setEmailVerificationError("");
    const result = await verifyGuestPlacementEmailCode(guestInfo.email, emailVerificationCode.trim());
    setEmailVerificationBusy(false);
    if (!result.ok || !result.token) {
      setEmailVerificationError(result.error || "The verification code is invalid.");
      return;
    }
    setEmailVerificationToken(result.token);
    setEmailVerificationOpen(false);
    await handleStartTest(result.token);
  };

  useEffect(() => {
    const prevDir = document.documentElement.dir;
    const prevLang = document.documentElement.lang;

    document.documentElement.dir = isArabic ? "rtl" : "ltr";
    document.documentElement.lang = policyLanguage;

    return () => {
      document.documentElement.dir = prevDir;
      document.documentElement.lang = prevLang;
    };
  }, [isArabic, policyLanguage]);

  // Pull lists/strings from i18n; provide defaults to be safe
  const prohibited = t("placementRules.prohibited", {
    returnObjects: true,
    defaultValue: isArabic
      ? [
          "نسخ أو تصوير الأسئلة.",
          "مشاركة محتوى الامتحان مع أي طرف آخر.",
          "استخدام أي وسائل غش إلكترونية أو خارجية.",
        ]
      : [
          "Copying or recording questions.",
          "Sharing the exam content with others.",
          "Using any electronic or external cheating methods.",
        ],
  });
  const after3 = t("placementRules.after3", {
    returnObjects: true,
    defaultValue: isArabic
      ? ["إلغاء امتحانك فورًا.", "حجبك (Block) من إعادة التسجيل في الامتحان."]
      : [
          "Your exam will be immediately cancelled.",
          "You will be permanently blocked from retaking the test.",
        ],
  });
  const sectionsNote = t("placementRules.sectionsNote", {
    returnObjects: true,
    defaultValue: isArabic
      ? [
          "١) الامتحان يتكون من أربعة أقسام: اختيار من متعدد، القراءة، الاستماع، والكتابة.",
          "٢) يسمح لك بأداء الامتحان مرة واحدة فقط.",
          "٣) استخدام أدوات الذكاء الاصطناعي أو أي وسائل غش يؤدي إلى إلغاء الامتحان وحجبك.",
          "٤) يجب توفر اتصال إنترنت مستقر.",
          "٥) مدة الامتحان 75 دقيقة.",
        ]
      : [
          "1) The test has four sections: MCQ, Reading, Listening, Writing.",
          "2) You are allowed to take the test only once.",
          "3) AI tools and cheating will result in termination and blacklisting.",
          "4) Stable internet is required.",
          "5) The test duration is 75 minutes.",
        ],
  });

  return (
    <>
      <Meta
        title={t("placementRules.metaTitle", {
          defaultValue: isArabic
            ? "قواعد اختبار تحديد المستوى | ALGermanist"
            : "Placement Test Rules | ALGermanist",
        })}
        description={t("placementRules.metaDesc", {
          defaultValue: isArabic
            ? "اقرأ القواعد والتعليمات قبل بدء اختبار تحديد المستوى."
            : "Read the rules and requirements before starting your German placement test.",
        })}
        keywords={t("placementRules.metaKeywords", {
          defaultValue: isArabic
            ? "اختبار تحديد المستوى, قواعد, تعليمات, ALGermanist"
            : "placement test, rules, instructions, ALGermanist",
        })}
      />
      <Box sx={{ p: { xs: 2, md: 4 }, direction: isArabic ? "rtl" : "ltr" }} dir={isArabic ? "rtl" : "ltr"}>
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Typography
            variant="h5"
            fontWeight="bold"
            gutterBottom
            textAlign={isArabic ? "right" : "left"}
          >
            {policy.title}
          </Typography>

          <Typography fontWeight={800} sx={{ mt: 2 }} textAlign={isArabic ? "right" : "left"}>
            {policy.introTitle}
          </Typography>
          <Typography sx={{ mb: 2 }} textAlign={isArabic ? "right" : "left"}>
            {policy.intro}
          </Typography>

          {!hasAccount && (
            <Box sx={{ p: 2, mb: 3, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fafafa" }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 800 }}>
                Continue as guest
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                <TextField
                  label="First name"
                  value={guestInfo.firstName}
                  onChange={(e) => setGuestInfo((p) => ({ ...p, firstName: e.target.value }))}
                  required
                />
                <TextField
                  label="Last name"
                  value={guestInfo.lastName}
                  onChange={(e) => setGuestInfo((p) => ({ ...p, lastName: e.target.value }))}
                  required
                />
                <TextField
                  label="Email"
                  type="email"
                  value={guestInfo.email}
                  onChange={(e) => { setGuestInfo((p) => ({ ...p, email: e.target.value })); setEmailVerificationToken(""); }}
                  required
                />
                <TextField
                  label="Phone number"
                  value={guestInfo.phoneNumber}
                  onChange={(e) => setGuestInfo((p) => ({ ...p, phoneNumber: e.target.value }))}
                  required
                />
              </Box>
            </Box>
          )}

          {false && <>
          <Typography fontWeight={600} sx={{ mt: 2 }} textAlign="left">
            {t("placementRules.prohibitedTitle", {
              defaultValue: isArabic ? "🚫 يُمنع منعًا باتًا:" : "🚫 Strictly prohibited:",
            })}
          </Typography>
          <List sx={{ listStyleType: "disc", pl: 3 }}>
            {prohibited.map((item, idx) => (
              <ListItem key={idx} sx={{ display: "list-item", py: 0 }}>
                <ListItemText
                  primaryTypographyProps={{ textAlign: "left" }}
                  primary={item}
                />
              </ListItem>
            ))}
          </List>

          <Typography sx={{ mt: 2 }} textAlign="left">
            {t("placementRules.detection", {
              defaultValue: isArabic
                ? "📌 النظام يرصد محاولات النسخ أو الغش تلقائيًا، وبعد ٣ محاولات فقط سيتم:"
                : "📌 The system automatically detects copying/cheating attempts. After 3 attempts only:",
            })}
          </Typography>
          <List sx={{ listStyleType: "disc", pl: 3 }}>
            {after3.map((item, idx) => (
              <ListItem key={idx} sx={{ display: "list-item", py: 0 }}>
                <ListItemText
                  primaryTypographyProps={{ textAlign: "left" }}
                  primary={item}
                />
              </ListItem>
            ))}
          </List>

          <Typography sx={{ mt: 2, mb: 2 }} textAlign="left">
            {t("placementRules.honesty", {
              defaultValue: isArabic
                ? "✅ التزم بالنزاهة، لأن مصلحتك الحقيقية هي أن نعرف مستواك الصحيح لضمان نجاحك في الكورس."
                : "✅ Be honest, because the only purpose of this test is to help you join the right course and achieve real results.",
            })}
          </Typography>

          <Box
            sx={{
              mt: 2,
              mb: 2,
              p: 2,
              borderRadius: 2,
              border: "1px solid #FDE68A",
              bgcolor: "#FFFCF0",
            }}
          >
            <Typography fontWeight={800} textAlign="left">
              {t("placementRules.attemptLimit", {
                defaultValue:
                  "You can take the placement test up to 3 times for the same target level. After the third attempt, starting another test for that level is blocked unless the team changes the policy.",
              })}
            </Typography>
          </Box>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 3 }} textAlign="left">
            {t("placementRules.legalTitle", {
              defaultValue: isArabic
                ? "تحذير قانوني بخصوص حقوق الملكية الفكرية"
                : "Legal Warning Regarding Intellectual Property",
            })}
          </Typography>
          <Typography sx={{ mt: 1 }} textAlign="left">
            {t("placementRules.legal1", {
              defaultValue: isArabic
                ? "جميع المواد التعليمية والفيديوهات والملفات المتوفرة على موقع ALGermanist هي ملكية فكرية خاصة بالشركة ومحمية بموجب قوانين حقوق الطبع والنشر وحماية الملكية الفكرية في مصر والقوانين الدولية."
                : "All learning materials, videos, and documents available on ALGermanist are the exclusive intellectual property of the Company. They are protected under Egyptian Copyright Law No. 82/2002 and international intellectual property regulations.",
            })}
          </Typography>
          <Typography sx={{ mt: 1 }} textAlign="left">
            {t("placementRules.legal2", {
              defaultValue: isArabic
                ? "يُمنع منعًا باتًا نسخ أو تصوير أو مشاركة أو إعادة نشر أي جزء من هذه المواد بأي وسيلة كانت (سواء إلكترونيًا أو ورقيًا أو عبر وسائل التواصل الاجتماعي) دون إذن كتابي وصريح من ALGermanist."
                : "It is strictly prohibited to copy, record, share, or redistribute any part of the content by any means (electronic, printed, or via social media) without prior written consent from ALGermanist.",
            })}
          </Typography>
          <Typography sx={{ mt: 1 }} textAlign="left">
            {t("placementRules.legal3", {
              defaultValue: isArabic
                ? "أي عملية تسريب أو استخدام غير مشروع للمحتوى ستُعرّض مرتكبها للمساءلة القانونية الكاملة، بما في ذلك المطالبة بالتعويضات المالية، واتخاذ الإجراءات الجنائية والمدنية وفقًا للقانون المصري والقوانين الدولية ذات الصلة."
                : "Any unauthorized use, leakage, or distribution of the materials will expose the violator to full legal liability, including financial compensation claims and civil/criminal legal actions in accordance with Egyptian and international law.",
            })}
          </Typography>

          <List sx={{ listStyleType: "decimal", pl: 3, mt: 2 }}>
            {sectionsNote.map((item, idx) => (
              <ListItem key={idx} sx={{ display: "list-item", py: 0 }}>
                <ListItemText
                  primaryTypographyProps={{ textAlign: "left" }}
                  primary={item}
                />
              </ListItem>
            ))}
          </List>
          </>}

          <Box sx={{ mt: 3 }}>
            {policy.sections.map((section) => (
              <Box
                key={section.title}
                component="section"
                sx={{
                  mb: 2.5,
                  p: { xs: 2, md: 2.5 },
                  border: "1px solid #E5E7EB",
                  borderRadius: 2,
                  bgcolor: "#FAFAFA",
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                  {section.title}
                </Typography>
                {section.items && (
                  <List
                    sx={{
                      listStyleType: "disc",
                      pr: isArabic ? 3 : 0,
                      pl: isArabic ? 0 : 3,
                      py: 0,
                    }}
                  >
                    {section.items.map((item) => (
                      <ListItem key={item} sx={{ display: "list-item", py: 0.35, textAlign: isArabic ? "right" : "left" }}>
                        <ListItemText primary={item} primaryTypographyProps={{ textAlign: isArabic ? "right" : "left" }} />
                      </ListItem>
                    ))}
                  </List>
                )}
                {section.paragraphs?.map((paragraph) => (
                  <Typography key={paragraph} sx={{ mt: 1, lineHeight: 1.8 }} textAlign={isArabic ? "right" : "left"}>
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
            <Button variant="outlined" color="inherit" onClick={() => navigate(-1)}>
              {t("common.back", { defaultValue: isArabic ? "العودة" : "Back" })}
            </Button>
            <Button variant="contained" onClick={() => handleStartTest()} disabled={emailVerificationBusy}>
              {t("placementRules.startBtn", {
                defaultValue: isArabic
                  ? "بدء اختبار تحديد المستوى"
                  : "Start Placement Test",
              })}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Dialog open={emailVerificationOpen} onClose={() => !emailVerificationBusy && setEmailVerificationOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{isArabic ? "تحقق من بريدك الإلكتروني" : isGerman ? "E-Mail bestätigen" : "Verify your email"}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{isArabic ? `أرسلنا رمزًا مكونًا من 6 أرقام إلى ${guestInfo.email}. أدخل الرمز للبدء.` : isGerman ? `Wir haben einen 6-stelligen Code an ${guestInfo.email} gesendet. Gib ihn ein, um zu starten.` : `We sent a 6-digit code to ${guestInfo.email}. Enter it to start the test.`}</Typography>
          <TextField autoFocus fullWidth label={isArabic ? "رمز التحقق" : isGerman ? "Bestätigungscode" : "Verification code"} value={emailVerificationCode} onChange={(event) => setEmailVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6))} inputProps={{ inputMode: "numeric", maxLength: 6, dir: "ltr" }} error={Boolean(emailVerificationError)} helperText={emailVerificationError} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEmailVerificationOpen(false); setEmailVerificationToken(""); }}>{isArabic ? "استخدام بريد آخر" : isGerman ? "Andere E-Mail" : "Use another email"}</Button>
          <Button variant="contained" onClick={verifyGuestEmailAndStart} disabled={emailVerificationBusy || emailVerificationCode.length !== 6}>{emailVerificationBusy ? (isArabic ? "جارٍ التحقق..." : isGerman ? "Wird geprüft..." : "Verifying...") : (isArabic ? "تحقق وابدأ" : isGerman ? "Bestätigen und starten" : "Verify and start")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fullscreenError} onClose={() => setFullscreenError(false)}>
        <DialogTitle>
          {loginRequired
            ? (isArabic ? "تسجيل الدخول مطلوب" : "Login required")
            : issueType === "start"
              ? (isArabic ? "تعذر بدء اختبار تحديد المستوى" : "Could not start placement test")
              : t("placementRules.dialogTitle", {
            defaultValue: isArabic
              ? "مشكلة في وضع ملء الشاشة / البدء"
              : "Fullscreen / Start Issue",
                })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {errorText ||
              t("placementRules.dialogBody", {
                defaultValue: isArabic
                  ? "يرجى السماح بوضع ملء الشاشة لبدء اختبار تحديد المستوى."
                  : "Please allow fullscreen mode to start the placement test.",
              })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullscreenError(false)}>
            {isArabic ? "إغلاق" : "Close"}
          </Button>
          {loginRequired && (
            <Button
              variant="contained"
              onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/placement/rules?targetLevel=${targetLevel}`)}`)}
            >
              {isArabic ? "تسجيل الدخول" : "Log in"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlacementTestRules;
